import React, { useState } from 'react';
import { Box, FormControl, Select } from '@mui/material';
import { LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import NorthIndianChartRenderer, { NorthIndianPlanetLabel } from './NorthIndianChartRenderer';
import { EastIndianChartRenderer, SouthIndianChartRenderer } from './NorthIndianChart';
import { getHouseShort, getPlanetAbbr, getSignAbbr } from '../../i18n/astrology';
import { isOuterPlanetKey, useUserPreferences } from '../../contexts/UserPreferencesContext';

interface PlanetEntry {
  name: string;
  longitude?: number;
  sign: { number: number; name: string };
  isRetrograde?: boolean;
  status?: string[];
}

interface UpagrahaEntry {
  name: string;
  shortName?: string;
  longitude?: number;
  degreeInSign?: number;
  meaning?: string;
  sign: { number: number; name: string };
}

interface DivisionalChartProps {
  chartData: {
    chartKey: string;
    chartName: string;
    purpose: string;
    lagna: { number: number; name: string };
    planets: { [key: string]: PlanetEntry };
    upagrahas?: { [key: string]: UpagrahaEntry };
  };
  size?: number;
  showUpagrahas?: boolean;
  transitChartData?: any;
  showTransitOverlay?: boolean;
}

function getPlanetTextColor(status: string[] = []): string {
  if (status.includes('E')) return '#1b7f2a';
  if (status.includes('Db') || status.includes('D')) return '#d32f2f';
  if (status.includes('V')) return '#7b1fa2';
  if (status.includes('R')) return '#e65100';
  if (status.includes('C')) return '#455a64';
  if (status.includes('A')) return '#00897b';
  return '#1b5e20';
}

function formatDeg(longitude: number | undefined): number | null {
  if (longitude === undefined) return null;
  return Math.floor(longitude % 30);
}

function buildPlanetEntry(key: string, status: string[], showDeg: boolean, longitude: number | undefined, t: any): NorthIndianPlanetLabel {
  const normalizedStatus = status.map((item) => item === 'Db' ? 'D' : item);

  return {
    abbr: getPlanetAbbr(t, key),
    degree: showDeg ? formatDeg(longitude) : null,
    status: normalizedStatus.join(''),
    color: getPlanetTextColor(status),
  };
}

function buildUpagrahaEntry(name: string, upagraha: UpagrahaEntry, showDeg: boolean): NorthIndianPlanetLabel {
  const degree = upagraha.degreeInSign ?? (upagraha.longitude !== undefined ? upagraha.longitude % 30 : null);

  return {
    abbr: upagraha.shortName || name.slice(0, 2),
    degree: showDeg && degree !== null ? Math.floor(degree) : null,
    color: '#7c3aed',
    isUpagraha: true,
    title: `${name}${degree !== null ? ` - ${Number(degree).toFixed(2)}°` : ''}${upagraha.meaning ? `\n${upagraha.meaning}` : ''}`,
  };
}

const DivisionalChart: React.FC<DivisionalChartProps> = ({
  chartData,
  size = 380,
  showUpagrahas = false,
  transitChartData,
  showTransitOverlay = false,
}) => {
  const [rotationOffset, setRotationOffset] = useState(0);
  const { t } = useTranslation();
  const { chartStyle, showOuterPlanets } = useUserPreferences();
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);

  const lagnaSignNumber = chartData.lagna.number;
  const showDeg = size >= 320;

  const getSignForHouse = (displayHouse: number): number => {
    const raw = (lagnaSignNumber - 1 + displayHouse - 1 + rotationOffset) % 12;
    return raw + 1;
  };

  const getPlanetsForHouse = (displayHouse: number) => {
    const targetSign = getSignForHouse(displayHouse);
    return getPlanetsForSign(targetSign);
  };

  const getPlanetsForSign = (targetSign: number) => {
    const planets = Object.entries(chartData.planets)
      .filter(([key, planet]) => shouldShowPlanet(key, planet))
      .filter(([, planet]) => planet.sign.number === targetSign)
      .map(([key, planet]) => {
        const status = planet.status ?? (planet.isRetrograde ? ['R'] : []);
        return buildPlanetEntry(key, status, showDeg, planet.longitude, t);
      });

    if (showUpagrahas) {
      Object.entries(chartData.upagrahas || {})
        .filter(([, upagraha]) => upagraha.sign?.number === targetSign)
        .forEach(([name, upagraha]) => {
          planets.push(buildUpagrahaEntry(name, upagraha, showDeg));
        });
    }

    if (showTransitOverlay) {
      Object.entries(transitChartData?.planets || {})
        .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
        .filter(([, planet]: any) => planet?.sign?.number === targetSign)
        .forEach(([key, planet]: any) => {
          const transitEntry = buildPlanetEntry(key, [], showDeg, planet?.longitude, t);
          planets.push({
            ...transitEntry,
            abbr: `T-${transitEntry.abbr}`,
            color: '#0369a1',
            isTransit: true,
            title: `${t('astrology.currentTransitPlanets')}: ${transitEntry.abbr}`,
          });
        });
    }

    return planets;
  };

  const house1Sign = getSignForHouse(1);
  const pivotLabel = rotationOffset === 0 ? '' : `(${getSignAbbr(t, house1Sign)} on ${getHouseShort(t, 1)})`;
  const houses = Array.from({ length: 12 }, (_, index) => {
    const id = index + 1;
    const signNumber = getSignForHouse(id);

    return {
      id,
      houseLabel: getHouseShort(t, id),
      signNumber,
      signLabel: getSignAbbr(t, signNumber),
      lagnaMarker: id === 1 && rotationOffset === 0 ? t('astrology.lagnaMarker') : undefined,
      planets: getPlanetsForHouse(id),
    };
  });
  const signFixedHouses = Array.from({ length: 12 }, (_, index) => {
    const sign = index + 1;
    return {
      id: sign,
      houseLabel: getHouseShort(t, ((sign - lagnaSignNumber + 12) % 12) + 1),
      signNumber: sign,
      signLabel: getSignAbbr(t, sign),
      lagnaMarker: sign === lagnaSignNumber ? t('astrology.lagnaMarker') : undefined,
      planets: getPlanetsForSign(sign),
    };
  });
  const isSignFixedStyle = chartStyle === 'south' || chartStyle === 'east';

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.25, color: 'primary.main', fontWeight: 600 }}>
        {chartData.chartName}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {chartData.purpose}
      </Typography>

      {!isSignFixedStyle && (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('astrology.rotateFromHouse')}</InputLabel>
          <Select
            value={rotationOffset + 1}
            label={t('astrology.rotateFromHouse')}
            onChange={(event) => setRotationOffset(Number(event.target.value) - 1)}
          >
            {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
              <MenuItem key={house} value={house}>{t('astrology.house', { number: house })}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      )}

      {pivotLabel && (
        <Typography variant="caption" sx={{ display: 'block', color: 'primary.dark', fontWeight: 600, mb: 0.25 }}>
          {pivotLabel}
        </Typography>
      )}

      {chartStyle === 'south' ? (
        <SouthIndianChartRenderer houses={signFixedHouses} size={size} ascSign={lagnaSignNumber} />
      ) : chartStyle === 'east' ? (
        <EastIndianChartRenderer houses={signFixedHouses} size={size} ascSign={lagnaSignNumber} />
      ) : (
        <NorthIndianChartRenderer
          houses={houses}
          size={size}
          borderColor="#2e7d52"
          gridColor="#aaa"
          houseColor="#1565C0"
          signColor="#C62828"
          style={{ borderRadius: 4 }}
        />
      )}
    </Box>
  );
};

export default DivisionalChart;
